import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { HOME } from "@/constants/testIds";

function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-slate-500">{label}</span>
      <input {...props} className="mt-1.5 w-full h-11 px-4 rounded-xl bg-white ring-1 ring-slate-200 focus:ring-[#0B132B] focus:outline-none text-sm" />
    </label>
  );
}

function TextArea({ label, ...props }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-slate-500">{label}</span>
      <textarea {...props} rows={4} className="mt-1.5 w-full px-4 py-3 rounded-xl bg-white ring-1 ring-slate-200 focus:ring-[#0B132B] focus:outline-none text-sm" />
    </label>
  );
}

export default function Colleges() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    college_name: "", contact_person: "", designation: "", phone: "", email: "",
    number_of_students: "", preferred_session: "", message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const upd = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/enquiries/college", form);
      toast.success("Thanks! We'll be in touch soon.");
      setOpen(false);
      setForm({ college_name: "", contact_person: "", designation: "", phone: "", email: "", number_of_students: "", preferred_session: "", message: "" });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  return (
    <section id="colleges" className="py-24 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-6">
          <div className="text-xs tracking-[0.28em] uppercase text-slate-500">For Colleges & Universities</div>
          <h2 className="mt-3 font-display text-3xl sm:text-5xl leading-tight text-[#0B132B]">Prepare Your Students Beyond the Classroom</h2>
          <p className="mt-5 text-slate-600 text-base sm:text-lg leading-relaxed">
            Be Hire Ready works with colleges and educational institutions to help students understand and develop the skills required to perform confidently during placement processes.
          </p>
          <ul className="mt-6 grid grid-cols-2 gap-x-6 gap-y-2.5 text-sm text-slate-600">
            {["Career awareness sessions","Interview preparation workshops","ATS resume workshops","Communication training","Group discussion training","Mock interviews","Placement-readiness programs","Employability development"].map((x) => (
              <li key={x} className="flex items-start gap-2 before:content-['·'] before:text-[#0B132B] before:mr-1">{x}</li>
            ))}
          </ul>
          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0B132B] text-white text-[11px] tracking-widest uppercase">Free Career Awareness Session</div>
          <div className="mt-6">
            <button onClick={() => setOpen(true)} data-testid={HOME.collegeCta}
                    className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#0B132B] text-white text-sm font-medium hover:-translate-y-0.5 transition-transform shadow-elev">
              Invite Be Hire Ready to Your College
            </button>
          </div>
        </div>

        <div className="lg:col-span-6 relative">
          <div className="relative rounded-3xl bg-gradient-to-br from-[#0B132B] to-[#1C2541] p-8 text-white overflow-hidden">
            <div className="absolute inset-0 grid-bg opacity-20" />
            <div className="relative">
              <div className="text-[11px] tracking-widest uppercase text-slate-400">Working with 3+ Institutions</div>
              <div className="mt-3 font-display text-2xl">Institution-ready programs</div>
              <div className="mt-6 grid grid-cols-2 gap-3">
                {["Awareness","Workshop","Resume Clinic","Mock Round"].map((t) => (
                  <div key={t} className="glass-dark rounded-xl p-4 text-sm">{t}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-[60] bg-[#0B132B]/60 backdrop-blur grid place-items-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl p-6 sm:p-8 w-full max-w-2xl shadow-high" data-testid="college-form">
            <div className="flex justify-between items-center">
              <div>
                <div className="text-xs tracking-widest uppercase text-slate-500">College Enquiry</div>
                <div className="font-display text-2xl text-[#0B132B]">Invite Be Hire Ready</div>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-500">✕</button>
            </div>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              <Field label="College Name" required value={form.college_name} onChange={upd("college_name")} />
              <Field label="Contact Person" required value={form.contact_person} onChange={upd("contact_person")} />
              <Field label="Designation" value={form.designation} onChange={upd("designation")} />
              <Field label="Phone" required value={form.phone} onChange={upd("phone")} />
              <Field label="Email" type="email" required value={form.email} onChange={upd("email")} />
              <Field label="Number of Students" value={form.number_of_students} onChange={upd("number_of_students")} />
              <Field label="Preferred Session" value={form.preferred_session} onChange={upd("preferred_session")} />
            </div>
            <div className="mt-4"><TextArea label="Message" value={form.message} onChange={upd("message")} /></div>
            <div className="mt-6 flex justify-end">
              <button disabled={submitting} data-testid="college-submit"
                      className="px-6 py-3 rounded-full bg-[#0B132B] text-white text-sm font-medium disabled:opacity-60">
                {submitting ? "Sending…" : "Submit Enquiry"}
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
